import { createRouter, createWebHashHistory, START_LOCATION } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import PlayView from '@/views/PlayView.vue'
import PlayerSelectorView from '@/views/PlayerSelectorView.vue'
import MatchSetupView from '@/views/MatchSetupView.vue'
import GameSelectionView from '@/views/GameSelectionView.vue'
import OnlineOverviewView from '@/views/OnlineOverviewView.vue'
import LobbyView from '@/views/LobbyView.vue'
import SetupSelectorView from '@/views/SetupSelectorView.vue'
import StarterSelectorView from '@/views/StarterSelectorView.vue'
import ProfileFriends from '@/views/profile/ProfileFriends.vue'
import StatsView from '@/views/profile/ProfileStats.vue'
import ProfileView from '@/views/ProfileView.vue'
import ProfileEditView from '@/views/ProfileEditView.vue'
import InboxView from '@/views/InboxView.vue'
import StatisticsView from '@/views/StatisticsView.vue'
import X01StatsView from '@/views/statistics/X01StatsView.vue'
import TacticsStatsView from '@/views/statistics/TacticsStatsView.vue'
import SegmentsStatsView from '@/views/statistics/SegmentsStatsView.vue'
import SetupsView from '@/views/SetupsView.vue'
import SetupCreateView from '@/views/SetupCreateView.vue'
import SetupEditView from '@/views/SetupEditView.vue'
import SetupDetailView from '@/views/SetupDetailView.vue'
import ProductSelectionView from '@/views/ProductSelectionView.vue'
import GameStatsView from '@/views/GameStatsView.vue'
import LoginView from '@/views/LoginView.vue'
import KillerView from '@/views/KillerView.vue'
import CamelRaceView from '@/views/CamelraceView.vue'
import { useAuthStore } from '@/stores/auth.js'
import { useMatchStore } from '@/stores/match.js'
import analytics from '@/services/analytics.ts'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'

// The screens between "game type picked" and "match running". Leaving these for anything other
// than the match itself means the user gave up somewhere in the setup funnel - the one thing
// pageviews alone can never show, because the drop-out is the absence of a next screen.
const SETUP_FUNNEL_ROUTES = new Set([
    'match-setup',
    'match-player-selector',
    'match-setup-selector',
    'match-starter-selector',
    'match-lobby',
])

// The screens a match is actually played on. Leaving one of these while the match has no winner
// yet is a walk-out: the quit rate, which no screen_view can express.
const PLAY_ROUTES = new Set(['play', 'match-play', 'killer', 'camelrace'])

// Leaving the play screen for one of these is not walking out. A rematch routes play ->
// starter-selector with a brand new match (no winner yet), which would otherwise read as a quit
// on the very flow that proves the opposite; the stats screen is part of finishing, not leaving.
const NOT_AN_ABANDON = new Set([...PLAY_ROUTES, ...SETUP_FUNNEL_ROUTES, 'match-game-stats'])

// When the current setup step was entered, so the abandon event can say how long the user sat on
// it before backing out.
let setupStepEnteredAt = null
const router = createRouter({
    history: createWebHashHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/login',
            name: 'login',
            component: LoginView,
            meta: { requiresGuest: true, hideMenu: true },
        },
        {
            path: '/',
            name: 'home',
            component: HomeView,
            meta: { requiresAuth: true, analytics: { screen: 'Game On - Home' } },
        },
        {
            path: '/games',
            redirect: { name: 'home' },
        },
        {
            path: '/play/local',
            name: 'game-selection-local',
            component: GameSelectionView,
            props: { online: false },
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/play/online',
            name: 'online-overview',
            component: OnlineOverviewView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/play/online/select',
            name: 'game-selection-online',
            component: GameSelectionView,
            props: { online: true },
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/matches/:matchId/setup',
            name: 'match-setup',
            component: MatchSetupView,
            meta: {
                requiresAuth: true,
                hideMenu: true,
                analytics: { screen: 'Game On - Game Setup' },
            },
        },
        {
            path: '/matches/:matchId/lobby',
            name: 'match-lobby',
            component: LobbyView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/player-selector',
            redirect: { name: 'home' },
        },
        {
            path: '/matchtype-selector',
            redirect: { name: 'home' },
        },
        {
            path: '/matchtype-settings-selector',
            redirect: { name: 'home' },
        },
        {
            path: '/setup-selector',
            redirect: { name: 'home' },
        },
        {
            path: '/starter-selector',
            name: 'starter-selector',
            component: StarterSelectorView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/play',
            name: 'play',
            component: PlayView,
            meta: {
                layout: 'GameLayout',
                requiresAuth: true,
                hideMenu: true,
                // Tracked by the view once the match type is known, so screen_view can carry game_type.
                analytics: { screen: 'Game On - Game', enabled: false },
            },
        },
        // Match-based routes
        {
            path: '/matches/:matchId/player-selector',
            name: 'match-player-selector',
            component: PlayerSelectorView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/matches/:matchId/setup-selector',
            name: 'match-setup-selector',
            component: SetupSelectorView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/matches/:matchId/starter-selector',
            name: 'match-starter-selector',
            component: StarterSelectorView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/matches/:matchId/play',
            name: 'match-play',
            component: PlayView,
            meta: {
                layout: 'GameLayout',
                requiresAuth: true,
                hideMenu: true,
                // Tracked by the view once the match type is known, so screen_view can carry game_type.
                analytics: { screen: 'Game On - Game', enabled: false },
            },
        },
        {
            path: '/matches/:matchId/game-stats',
            name: 'match-game-stats',
            component: GameStatsView,
            meta: {
                requiresAuth: true,
                hideMenu: true,
                analytics: { screen: 'Game On - Game Stats' },
            },
        },
        {
            path: '/profile',
            name: 'profile',
            component: ProfileView,
            redirect: { name: 'profile-stats' },
            meta: { requiresAuth: true },
            children: [
                {
                    path: 'stats',
                    name: 'profile-stats',
                    component: StatsView,
                    meta: { requiresAuth: true, analytics: { screen: 'Game On - Profile' } },
                },
                {
                    path: 'friends',
                    name: 'profile-friends',
                    component: ProfileFriends,
                    meta: { requiresAuth: true, analytics: { screen: 'Game On - Friends' } },
                },
            ],
        },
        {
            path: '/profile/edit',
            name: 'profile-edit',
            component: ProfileEditView,
            meta: {
                requiresAuth: true,
                hideMenu: true,
                analytics: { screen: 'Game On - Edit Profile' },
            },
        },
        {
            path: '/inbox',
            name: 'inbox',
            component: InboxView,
            meta: {
                requiresAuth: true,
                hideMenu: true,
                analytics: { screen: 'Game On - Notifications' },
            },
        },
        {
            path: '/statistics',
            name: 'statistics',
            component: StatisticsView,
            meta: {
                requiresAuth: true,
                navTab: 'statistics',
                analytics: { screen: 'Game On - Statistics' },
            },
        },
        {
            path: '/statistics/x01',
            name: 'statistics-x01',
            component: X01StatsView,
            meta: {
                requiresAuth: true,
                navTab: 'statistics',
                analytics: { screen: 'Game On - Statistics X01' },
            },
        },
        {
            path: '/statistics/tactics',
            name: 'statistics-tactics',
            component: TacticsStatsView,
            meta: {
                requiresAuth: true,
                navTab: 'statistics',
                analytics: { screen: 'Game On - Statistics Tactics' },
            },
        },
        {
            path: '/statistics/segments',
            name: 'statistics-segments',
            component: SegmentsStatsView,
            meta: {
                requiresAuth: true,
                navTab: 'statistics',
                analytics: { screen: 'Game On - Statistics Segments' },
            },
        },
        {
            path: '/killer',
            name: 'killer',
            component: KillerView,
            meta: {
                requiresAuth: true,
                hideMenu: true,
                // Tracked by the view once the match type is known, so screen_view can carry game_type.
                analytics: { screen: 'Game On - Game', enabled: false },
            },
        },
        {
            path: '/setups',
            name: 'setups',
            component: SetupsView,
            meta: {
                requiresAuth: true,
                // The tabs inside SetupsView sync themselves into ?tab=, so the screen name can be
                // derived from the query here instead of being tracked by the component.
                analytics: {
                    screen: (to) =>
                        to.query.tab === 'explore'
                            ? 'Game On - Setups - Explore'
                            : 'Game On - Setups - My Setups',
                },
            },
        },
        {
            path: '/setups/create',
            name: 'setup-create',
            component: SetupCreateView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/setups/select-product',
            name: 'setup-select-product',
            component: ProductSelectionView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/setups/:id/edit',
            name: 'setup-edit',
            component: SetupEditView,
            meta: { requiresAuth: true, hideMenu: true },
        },
        {
            path: '/setups/:id',
            name: 'setup-detail',
            component: SetupDetailView,
            meta: { requiresAuth: true, hideMenu: false },
        },
        {
            path: '/camelrace',
            name: 'camelrace',
            component: CamelRaceView,
            meta: {
                // Tracked by the view once the match type is known, so screen_view can carry game_type.
                analytics: { screen: 'Game On - Game', enabled: false },
            },
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        // Hash-only change on the same route - the only thing route hashes are
        // used for in this app is toggling a drawer/modal open or closed (see
        // Drawer.vue/SimpleDrawer.vue's hashKey prop), never a real anchor to
        // scroll to. Leave the scroll position exactly where it is.
        if (to.path === from.path) {
            return false
        }

        // Plain navigation (no back/forward, no anchor) - reset instantly.
        // This doesn't depend on the new view's layout, so unlike the cases
        // below it doesn't need to wait for anything: an animated reset here
        // only left the container visibly mid-scroll for a few hundred ms,
        // which a screenshot taken right after navigating could catch.
        if (!savedPosition && !to.hash) {
            const scrollContainer = document.getElementById('scroll-container')
            if (scrollContainer) {
                scrollContainer.scrollTo({ top: 0, left: 0, behavior: 'auto' })
            }
            return { top: 0, left: 0 }
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                const scrollContainer = document.getElementById('scroll-container')

                if (scrollContainer) {
                    // If there's a saved position (back/forward navigation), use it
                    if (savedPosition) {
                        scrollContainer.scrollTo({
                            top: savedPosition.top,
                            left: savedPosition.left || 0,
                            behavior: 'smooth',
                        })
                        resolve(savedPosition)
                    }
                    // If there's a hash, scroll to that element
                    else if (to.hash) {
                        const element = document.querySelector(to.hash)
                        if (element) {
                            const top = element.offsetTop
                            scrollContainer.scrollTo({
                                top,
                                left: 0,
                                behavior: 'smooth',
                            })
                            resolve({ top, left: 0 })
                        } else {
                            resolve({ top: 0, left: 0 })
                        }
                    }
                } else {
                    // Fallback if scroll container doesn't exist
                    resolve(savedPosition || { top: 0, left: 0 })
                }
            }, 100)
        })
    },
})

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()

    // If we have a token but no user (token verification in progress), wait for it
    if (authStore.token && !authStore.user) {
        // Wait for token verification to complete
        let attempts = 0
        while (authStore.token && !authStore.user && attempts < 50) {
            // Max 5 seconds
            await new Promise((resolve) => setTimeout(resolve, 100))
            attempts++
        }
    }

    // Check authentication first
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        next({ name: 'login' })
        return
    } else if (to.meta.requiresGuest && authStore.isAuthenticated) {
        next({ name: 'home' })
        return
    }

    const isMatchRoute = to.name?.startsWith('match-')
    if (isMatchRoute && to.params?.matchId) {
        return next()
    }
    return next()
})

router.afterEach((to, from) => {
    // Manual screen_view tracking: automatic screen tracking is disabled on the native side, so
    // every navigation (including native back/forward) reports here. Same-path re-resolves are
    // skipped so redirects don't double-count - except for the very first navigation, whose `from`
    // is START_LOCATION and already reports fullPath '/', which would otherwise silently drop the
    // screen_view for the app's most common entry point: a cold start on Home.
    if (from === START_LOCATION || to.fullPath !== from.fullPath) {
        analytics.trackPageView(to, from)
    }

    const leftSetupFunnel = SETUP_FUNNEL_ROUTES.has(from.name)
    const enteredSetupFunnel = SETUP_FUNNEL_ROUTES.has(to.name)
    const startedPlaying = to.name === 'play' || to.name === 'match-play'

    if (leftSetupFunnel && !enteredSetupFunnel && !startedPlaying) {
        analytics.trackEvent(ANALYTICS_EVENTS.MATCH_SETUP_ABANDONED, {
            step: from.name,
            next_screen: to.name ?? null,
            ...(from.params?.matchId ? { match_id: String(from.params.matchId) } : {}),
            ...(setupStepEnteredAt
                ? { seconds_in_step: Math.round((Date.now() - setupStepEnteredAt) / 1000) }
                : {}),
        })
    }

    setupStepEnteredAt = enteredSetupFunnel ? Date.now() : null

    const wasMatchRoute = from.name === 'play' || from.name?.startsWith('match-')
    const isMatchRoute = to.name === 'play' || to.name?.startsWith('match-')

    if (PLAY_ROUTES.has(from.name) && !NOT_AN_ABANDON.has(to.name)) {
        const matchStore = useMatchStore()

        if (!matchStore.isMatchOver) {
            analytics.trackEvent(ANALYTICS_EVENTS.MATCH_ABANDONED, {
                next_screen: to.name ?? null,
                ...(matchStore.match?.id ? { match_id: String(matchStore.match.id) } : {}),
                ...(matchStore.matchStartedAt
                    ? {
                          duration_sec: Math.round(
                              (Date.now() - matchStore.matchStartedAt) / 1000,
                          ),
                      }
                    : {}),
            })
        }
    }

    if (wasMatchRoute && !isMatchRoute) {
        const matchStore = useMatchStore()

        if (matchStore.activeChannel) {
            console.log('Router: Cleaning up match channel on navigation away from match')
            setTimeout(() => {
                matchStore.leaveMatchChannel()
            }, 100)
        }

        if (matchStore.isMatchOver) {
            matchStore.clearMatch()
        }
    }
})

export default router
